import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import {
   DialogActions,
   DialogContent,
   DialogTitle,
   FormControl,
   Pagination,
   Tab,
   Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import profile from '../../../assets/images/logo/profile.png';
import {
   ControlpanelErrorButton,
   ControlpanelSecondaryButton,
   ControlpanelSuccessButton,
   GrayIconButton,
} from '../../../components/StyledButton';
import { CustomDialog, CustomTabPanel, CustomTabsV2 } from '../../../components/StyledComponents';
import CustomInput from '../../../components/CustomInput';
import { isValid, parse, format } from 'date-fns';
import { useAuth } from '../../../context/AuthContext';
import CorfirmApproveRequestDialog from './CorfirmApproveRequestDialog';
import CorfirmRejectRequestDialog from './CorfirmRejectRequestDialog';
import { SpinnerRing } from '../../../components/CustomSpinner';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ImageIcon from '@mui/icons-material/Image';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DoneIcon from '@mui/icons-material/Done';
import { toast } from 'react-toastify';
import { getImgEvent, requestById, getHistoryRequest } from '../../../axios/Eventlog/EventLogApi';
import ClearIcon from '@mui/icons-material/Clear';

dayjs.extend(utc);
dayjs.extend(timezone);

const DetailRequestDialog = ({
   onClose,
   usersData,
   shiftsData,
   onRefresh,
   info,
   parsedNewData,
   parsedOldData,
   requestHistory,
   onStatusResolved,
}) => {
   const { genaralSettingDateFormat, profile: authProfile, defaultReason, genaralSettingTimeZone, dateTimeFormat } = useAuth();
   const { t } = useTranslation();

   const [openCorfirmApproveRequest, setOpenCorfirmApproveRequest] =
      useState(false);
   const [openCorfirmRejectRequest, setOpenCorfirmRejectRequest] =
      useState(false);
   const MAX_REASON_LENGTH = 255
   const [loadingCount, setLoadingCount] = useState(0);
   const isLoading = loadingCount > 0;
   const [dataIMG, setDataIMG] = useState([])
   const [page, setPage] = useState(0);
   const [pageSize, setPageSize] = useState(50);
   const [rowCount, setRowCount] = useState(0);
   const [selectedImageId, setSelectedImageId] = useState(null);
   const [isImageSectionOpen, setIsImageSectionOpen] = useState(false);
   const [formData, setFormData] = useState(null);
   const [tabValue, setTabValue] = useState(0);
   const [requestDetail, setRequestDetail] = useState(() => {
      const initial = Array.isArray(info) ? info[0] : info;
      return initial || null;
   });
   const [localHistory, setLocalHistory] = useState(() =>
      Array.isArray(requestHistory) ? requestHistory : []
   );

   const handleChangeTab = useCallback((event, newValue) => {
      setTabValue(newValue);
   }, []);

   const a11yProps = (index) => ({
      id: `user-tab-${index}`,
      'aria-controls': `user-tabpanel-${index}`,
   });

   const RECORD_PER_PAGE = 10;

   function AvatarCard({
      fullName,
      time,
      imageBase64,
      eventTypeId,
      isSelected,
      onSelect
   }) {
      const localTime = dayjs.utc(time).local().format("HH:mm:ss");

      return (
         <div className="text-center w-full flex flex-col">
            {/* IMAGE CONTAINER */}
            <div
               className={`
         relative h-32 w-32 overflow-hidden rounded-lg
         ${isSelected ? "ring-2 ring-blue-500" : ""}
         group cursor-pointer flex items-center justify-center
      `}
               onClick={onSelect}
            >
               <img
                  src={imageBase64}
                  alt={fullName || t('common.unidentified-user')}
                  className="w-full h-full object-cover transition-all duration-200"
               />

               {/* Hover dark overlay */}
               <div
                  className="
            absolute inset-0 bg-black/40 opacity-0
            group-hover:opacity-100
            transition-opacity duration-200
         "
               />

               {/* Hover border */}
               <div
                  className="
            absolute inset-0 rounded-lg
            ring-0 group-hover:ring-2 group-hover:ring-blue-500
            transition-all duration-200
            pointer-events-none
         "
               />

               {/* Selected check icon */}
               {isSelected && (
                  <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-md">
                     <DoneIcon style={{ width: '16px', height: '16px' }} />
                  </div>
               )}
            </div>

            {/* NAME */}
            <div className="mt-2 font-semibold text-sm text-left">
               {fullName || t('common.unidentified-user')}
            </div>

            {/* TIME */}
            <div className="flex items-center gap-1 mt-1 text-sm text-gray-600">
               <AccessTimeIcon style={{ width: '16px', height: '17px' }} />
               <span>{localTime}</span>
            </div>
         </div>
      );
   }

   const getFromToByDateWithTZ = (dateObj, timeZone) => {
      if (!dateObj) return { from: null, to: null };

      const base = dayjs(dateObj).tz(timeZone);

      return {
         from: base.startOf("day").utc().toISOString(),
         to: base.endOf("day").utc().toISOString(),
      };
   };

   const handleCloseDialog = () => {
      onClose();
   };

   const browserTZ = dayjs.tz.guess();

   const parseAttendanceDateTimeByCompanyTZ = (isoUtc) => {
      if (!isoUtc || !browserTZ) {
         return { date: '', time: '' };
      }

      const companyDateTime = dayjs
         .utc(isoUtc)                 // backend luôn UTC
         .tz(browserTZ); // timezone công ty

      return {
         date: companyDateTime.format('YYYY-MM-DD'),
         time: companyDateTime.format('HH:mm:ss'),
      };
   };

   const shiftMap = useMemo(() => {
      const map = {};
      (shiftsData || []).forEach(shift => {
         map[shift.shiftId] = shift.shiftName;
      });
      return map;
   }, [shiftsData]);

   const userMap = useMemo(() => {
      const map = {};
      (usersData || []).forEach(user => {
         map[user.userId] = user.fullName;
      });
      return map;
   }, [usersData]);

   const getFullNameByEmail = (email) => {
      if (!email) return "-";

      const normalizedEmail = String(email).trim().toLowerCase();

      const user = usersData.find(
         (u) =>
            u.email &&
            u.email.trim().toLowerCase() === normalizedEmail
      );

      // nếu tìm được thì trả fullName, không thì fallback về email
      return user?.fullName || email;
   };

   // ----- Chuẩn hóa info (ưu tiên data đã fetch trong dialog) -----
   const safeInfo = Array.isArray(requestDetail)
      ? requestDetail[0]
      : requestDetail || (Array.isArray(info) ? info[0] : info) || {};

   // Fetch detail + history ngay khi mở popup (giống LeaveAndHoliday)
   useEffect(() => {
      const initialInfo = Array.isArray(info) ? info[0] : info;
      const requestId = initialInfo?.id;
      if (!requestId) return;

      let cancelled = false;

      const fetchDetail = async () => {
         try {
            setLoadingCount((prev) => prev + 1);
            const res = await requestById({
               requestId: Number(requestId),
               langId: localStorage.getItem("i18nextLngId"),
            });
            const raw = res?.data?.data || res?.data?.request || res?.data;
            const data = Array.isArray(raw) ? raw[0] : raw;

            if (cancelled) return;

            if (!data) {
               toast.error(t("common.cannot-find-request"));
               onClose();
               return;
            }

            const allowedTypes = ["ADD_EVENT_LOG", "EDIT_EVENT_LOG"];
            if (!allowedTypes.includes(data.requestTypeName)) {
               toast.error(t("common.cannot-find-request"));
               onClose();
               return;
            }

            setRequestDetail(data);
            // Deep link / mở nhầm dialog: báo parent chuyển sang AR nếu không còn PENDING
            if (
               typeof onStatusResolved === "function" &&
               data.requestStatusName &&
               data.requestStatusName !== "PENDING"
            ) {
               onStatusResolved(data);
            }
         } catch (error) {
            if (!cancelled) {
               console.error(error);
               toast.error(t("common.cannot-find-request"));
               onClose();
            }
         } finally {
            if (!cancelled) {
               setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
         }
      };

      const fetchHistory = async () => {
         try {
            setLoadingCount((prev) => prev + 1);
            const response = await getHistoryRequest({
               requestId: Number(requestId),
               langId: localStorage.getItem("i18nextLngId"),
            });
            if (!cancelled) {
               setLocalHistory(response?.data?.data || []);
            }
         } catch (error) {
            console.log(error);
            if (!cancelled) {
               setLocalHistory([]);
            }
         } finally {
            if (!cancelled) {
               setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
         }
      };

      fetchDetail();
      fetchHistory();

      return () => {
         cancelled = true;
      };
   }, [info?.id]);

   // Helper parse payload (hỗ trợ cả string lẫn object)
   const parsePayload = (data) => {
      if (!data) return {};

      // Nếu backend trả dạng string JSON
      if (typeof data === 'string') {
         try {
            return JSON.parse(data);
         } catch (e) {
            console.error('Parse payload error (string):', e, data);
            return {};
         }
      }

      // Nếu backend đã trả sẵn dạng object
      if (typeof data === 'object') {
         return data;
      }

      return {};
   };

   // ----- Parse newData / oldData chỉ 1 lần -----
   const autoParsedNew = parsePayload(safeInfo.newData);
   const autoParsedOld = parsePayload(safeInfo.oldData);

   // Ưu tiên props parsed* nếu được truyền từ ngoài, nếu không thì dùng autoParsed*
   const safeNew = autoParsedNew;

   const safeOld = autoParsedOld;

   const tryParseDate = (dateStr) => {
      if (!dateStr) return null;

      const formatsToTry = ['yyyy-MM-dd HH:mm:ss', 'yyyy-MM-dd'];
      for (const fmt of formatsToTry) {
         const parsedDate = parse(dateStr, fmt, new Date());
         if (isValid(parsedDate)) return parsedDate;
      }

      return null;
   };

   // ----- Helper build initial formData -----
   const buildInitialFormData = () => {
      const attendance = safeNew.attendanceDateTime
         ? parseAttendanceDateTimeByCompanyTZ(
            safeNew.attendanceDateTime
         )
         : null;

      // oldData: ưu tiên attendanceDateTime (EDIT_EVENT_LOG), fallback eventDate/eventTime
      const attendanceOld = safeOld.attendanceDateTime
         ? parseAttendanceDateTimeByCompanyTZ(safeOld.attendanceDateTime)
         : null;

      // user / username
      const targetUserId = safeInfo.userId ?? safeNew.userId;

      const user = Array.isArray(usersData)
         ? usersData.find((u) => u.userId === targetUserId)
         : null;

      const username =
         user?.fullName ?? safeInfo.fullName ?? safeInfo.userName ?? '';

      // shift
      const shift = Array.isArray(shiftsData)
         ? shiftsData.find((item) => item.shiftId === safeNew.shiftId)
         : null;

      // event type + time
      const isEditEvent = !!(safeNew.date || safeNew.time);

      const createTime = dayjs
         .utc(safeInfo.createdTime)
         .local()
         .format(dayjsFormat);

      let time = '';
      if (isEditEvent) {
         time = safeNew.time || '';
      } else {
         const isCheckIn = safeNew.eventTypeId === 1;
         const fullTime = isCheckIn ? safeNew.timeCheckIn : safeNew.timeCheckOut;
         time = fullTime?.split(' ')[1] || '';
      }

      const dateFormatPattern = genaralSettingDateFormat || 'dd/MM/yyyy';

      const newDateObj = !isEditEvent
         ? tryParseDate(
            safeNew.eventTypeId === 1 ? safeNew.timeCheckIn : safeNew.timeCheckOut
         )
         : tryParseDate(safeNew.date);

      const oldDateObj = !attendanceOld && safeOld.eventDate
         ? tryParseDate(safeOld.eventDate)
         : null;

      const newTime =
         safeNew?.time ||
         (safeNew?.timeCheckIn ? safeNew.timeCheckIn.split(' ')[1] : '');

      const oldTime =
         safeOld?.eventTime ||
         (safeOld?.timeCheckIn ? safeOld.timeCheckIn.split(' ')[1] : '');

      const getShiftDisplayName = (shift) => {
         if (!shift) return '';

         const rawName = shift.shiftName || shift.name || '';

         // nếu là DEFAULT → dùng i18n
         if (rawName.toUpperCase() === 'DEFAULT') {
            return t('common.default');
         }

         return rawName;
      };

      return {
         eventType: safeNew?.eventTypeId ? safeNew.eventTypeId : 0,
         username,

         selectDate: attendance
            ? format(new Date(attendance.date), dateFormatPattern)
            : '',

         selectDateRaw: attendance?.date
            ? dayjs(attendance.date).toDate()
            : null,
         selectDateOld: attendanceOld?.date
            ? format(new Date(attendanceOld.date), dateFormatPattern)
            : oldDateObj
               ? format(oldDateObj, dateFormatPattern)
               : '',

         // selectTime: time || '00:00:00',
         // selectTimeOld: safeOld?.eventTime ? safeOld.eventTime : '',

         selectTime: attendance?.time || '',
         selectTimeOld: attendanceOld?.time || oldTime || '',

         timeZone: safeNew?.timeZone || '',
         timeZoneOld: safeOld?.timeZone || '',

         shift: shift ? shift.shiftId : '',
         shiftOld: (() => {
            if (!safeOld?.shiftId || !Array.isArray(shiftsData)) {
               return getShiftDisplayName({
                  shiftName: safeOld?.shiftName,
                  name: safeOld?.shiftName,
               });
            }
            const oldShift = shiftsData.find(
               (s) => s.shiftId === safeOld.shiftId
            );
            return getShiftDisplayName(oldShift || { shiftName: safeOld?.shiftName });
         })(),
         shiftNew: getShiftDisplayName(shift),
         location: safeNew?.location || safeOld?.location || safeInfo?.location || '',

         descriptionOld: safeInfo?.requestReason || '',
         device: safeInfo?.deviceDescription || '',

         // lý do approve/reject do admin nhập
         description: '' || defaultReason,
         userId: authProfile.userId,
         requestTypeId: 2,
         eventAttendanceId: safeInfo.id,
         shiftIdOld: safeNew.shiftId,
         requestId: safeInfo?.id ? safeInfo.id : '',
         createTime,
      };
   };

   useEffect(() => {
      if (!safeInfo || !safeNew || Object.keys(safeNew).length === 0) return;

      const built = buildInitialFormData();
      setFormData(built);
   }, [
      requestDetail,
      info,
      parsedNewData,
      parsedOldData,
      usersData,
      shiftsData,
      genaralSettingDateFormat,
      authProfile,
      defaultReason,
   ]);

   const handleInputChange = (e) => {
      const { name, value } = e.target;

      if (name === 'description' && value.length > MAX_REASON_LENGTH) {
         return; // không cập nhật nếu quá 255 ký tự
      }

      setFormData((prevData) => ({
         ...prevData,
         [name]: value,
      }));
   };

   const handleOpenApproveRequest = () => {
      setOpenCorfirmApproveRequest(true);
   };

   const handleCloseApproveRequest = () => {
      setOpenCorfirmApproveRequest(false);
   };

   const handleOpenRejectRequest = () => {
      setOpenCorfirmRejectRequest(true);
   };

   const handleCloseRejectRequest = () => {
      setOpenCorfirmRejectRequest(false);
   };

   useEffect(() => {
      if (!formData) return;

      const browserTZ = dayjs.tz.guess();
      const { from, to } = getFromToByDateWithTZ(
         formData?.selectDateRaw,
         browserTZ
      );

      const fetchData = async () => {
         const payload = {
            page: page + 1,
            record: RECORD_PER_PAGE,
            from,
            to,
            isError: true,
            langId: localStorage.getItem("i18nextLngId")
         };

         try {
            setLoadingCount((prev) => prev + 1);
            const response = await getImgEvent(payload);

            const imgList = response.data.data || [];

            const sortedImgList = [...imgList]
               .sort((a, b) => dayjs.utc(b.time).valueOf() - dayjs.utc(a.time).valueOf())
               .map((item, index) => ({
                  ...item,
                  uniqueId: `${item.time}-${index}`
               }));

            setDataIMG(sortedImgList);

            setDataIMG(sortedImgList);
            setRowCount(response.data.pagination.totalElements);
         } catch (error) {
            toast.error('Load image error');
         } finally {
            setLoadingCount((prev) => Math.max(prev - 1, 0));
         }
      };

      fetchData();

   }, [
      formData?.selectDate,  // ✅ đổi ở đây
      page,
      pageSize
   ]);

   const hasOldData = Object.keys(safeOld || {}).length > 0;
   const isEditEventLog =
      String(safeInfo?.requestTypeName || "").toUpperCase() === "EDIT_EVENT_LOG";
   const showEditEventComparison = isEditEventLog || hasOldData;

   const oldDateTimeText =
      formData?.selectDateOld && formData?.selectTimeOld
         ? `${formData.selectDateOld} ${formData.selectTimeOld}`
         : formData?.selectDateOld || formData?.selectTimeOld || "";
   const newDateTimeText =
      formData?.selectDate && formData?.selectTime
         ? `${formData.selectDate} ${formData.selectTime}`
         : formData?.selectDate || "";
   const comparisonRowClassName =
      "grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)] gap-3 items-end w-full";
   const comparisonArrow = (
      <div className="hidden md:flex justify-center text-2xl text-[#525252] pb-1.5">→</div>
   );

   const convertDateFnsToDayjsFormat = (format) => {
      if (!format) return 'DD/MM/YYYY';

      return format
         .replace(/yyyy/g, 'YYYY')
         .replace(/yy/g, 'YY')
         .replace(/dd/g, 'DD')
         .replace(/d/g, 'D');
   };

   const dayjsFormat = convertDateFnsToDayjsFormat(dateTimeFormat);

   const parsedHistory = useMemo(() => {
      return localHistory?.map(item => {
         let data = {};
         try {
            data = item.newData
               ? (typeof item.newData === "string" ? JSON.parse(item.newData) : item.newData)
               : {};
         } catch {
            data = {};
         }

         const createdAtRaw = item?.createdAt || item?.createdTime || null;
         const createdAtLabel = createdAtRaw
            ? dayjs.utc(createdAtRaw).local().format(dayjsFormat)
            : "-";

         return {
            ...item,
            createdAt: createdAtRaw,
            createdAtLabel,
            data: {
               ...data,
               shiftName: shiftMap[data.shiftId],
               userName: userMap[data.userId],
               eventTypeName:
                  data.eventTypeId === 1
                     ? t('attendance.CHECK_IN')
                     : t('attendance.CHECK_OUT'),
               eventTypeColor:
                  data.eventTypeId === 1
                     ? '#058900'
                     : '#0057FF',
               attendanceDate: data.attendanceDateTime
                  ? dayjs
                     .utc(data.attendanceDateTime)
                     .local()
                     .format(dayjsFormat)
                  : '',
               createdBy: item.createdBy
            }
         };
      }) || [];
   }, [localHistory, shiftMap, userMap, t, dayjsFormat]);

   // Hiện popup liền; chưa có formData → DotLoader; đang fetch → overlay (giống LeaveAndHoliday)
   if (!formData) {
      return (
         <CustomDialog maxWidth={'xl'} open onClose={undefined} keepMounted>
            <div className="flex items-center justify-center min-h-[200px]">
               <SpinnerRing loading size={50} color="#3B82F6" />
            </div>
         </CustomDialog>
      );
   }

   return (
      <>
         <CustomDialog
            maxWidth={'xl'}
            open
            loading={isLoading}
            onClose={handleCloseDialog}
            keepMounted
         >
            <DialogTitle>
               <div className='flex justify-between'>
                  <div className='flex items-center gap-4'>
                     <p className='text-xl font-semibold '>
                        {safeInfo?.requestTypeName === "EDIT_EVENT_LOG" ? t('common.request-edit-event') : t('common.request-new-event')}
                     </p>
                  </div>
                  <GrayIconButton onClick={handleCloseDialog} disabled={isLoading}>
                     <CloseIcon />
                  </GrayIconButton>
               </div>
            </DialogTitle>

            <DialogContent>
               {safeInfo.image && (
                  <div className='pt-5'>
                     <div className='flex justify-between items-start pb-5'>
                        <div className='p-1 rounded border border-solid border-[#E5E7EB] max-w-40'>
                           <img
                              src={`data:image/png;base64,${safeInfo.image}`}
                              alt='avatar'
                           />
                        </div>
                        <div>
                           <img src={profile} alt='profile' />
                        </div>
                     </div>
                  </div>
               )}

               <div className=''>
                  <CustomTabsV2
                     variant="scrollable"
                     indicatorColor="#FFFFFF"
                     value={tabValue}
                     onChange={handleChangeTab}
                  >
                     <Tab label={t('button.detail')} {...a11yProps(0)} disableRipple />
                     <Tab label={t('common.history')} {...a11yProps(1)} disableRipple />
                  </CustomTabsV2>
               </div>

               <CustomTabPanel value={tabValue} index={0}>
                  <div className='grid grid-cols-12 md:gap-x-5 gap-y-3 mt-[20px]'>
                     <div className='col-span-12 md:col-span-6 flex flex-col gap-2'>
                        <div className='text-[#171717]'>
                           <p>{t('common.status')}</p>
                        </div>
                        <div className='col-span-4'>
                           {safeInfo?.requestStatusName === "APPROVED" && (
                              <p className="text-[#22C55E] font-semibold">
                                 {t("common.approved")}
                              </p>
                           )}
                           {safeInfo?.requestStatusName === "PENDING" && (
                              <p className="text-[#EAB308] font-semibold">
                                 {t("common.pending-request")}
                              </p>
                           )}
                           {safeInfo?.requestStatusName === "REJECTED" && (
                              <p className="text-[#EF4444] font-semibold">
                                 {t("common.rejected")}
                              </p>
                           )}
                        </div>
                     </div>

                     {/* Event type */}
                     <div className='col-span-12 md:col-span-6 flex flex-col gap-2'>
                        <p className='text-[#171717]'>
                           {t('common.event-type')}
                        </p>

                        <div className='flex-1'>
                           <FormControl
                              component='fieldset'
                              className='w-full'
                              sx={{
                                 '.MuiFormGroup-root.MuiFormGroup-row': {
                                    gap: '20px',
                                    alignItems: 'center',
                                    pointerEvents: 'none',
                                 },
                              }}
                           >
                              <div className='flex w-full items-center justify-between'>
                                 <div className="flex items-center gap-1 font-semibold">
                                    <span
                                       className={`font-semibold ${formData.eventType === 2 ? 'text-blue-500' : 'text-green-500'
                                          }`}
                                    >
                                       {formData.eventType === 2
                                          ? t('attendance.CHECK_OUT')
                                          : t('attendance.CHECK_IN')}
                                    </span>
                                 </div>

                              </div>
                           </FormControl>
                        </div>
                     </div>

                     {/* Username - full width */}
                     <div className='col-span-12 flex flex-col gap-2'>
                        <label className='text-[#171717]'>{t('common.userName')}</label>
                        <div className='w-full'>
                           <CustomInput
                              name='username'
                              inputType='text'
                              readOnly={true}
                              value={formData.username}
                              handleInputChange={handleInputChange}
                              labelEnable={false}
                              className='w-full'
                              placeholder={t('common.userName')}
                           />
                        </div>
                     </div>

                     {/* EDIT_EVENT_LOG: so sánh cũ → mới */}
                     {showEditEventComparison ? (
                        <>
                           <div className='col-span-12 flex flex-col gap-2'>
                              <label className='text-[#171717]'>{t('common.request-time')}</label>
                              <div className='w-full'>
                                 <CustomInput
                                    inputType='text'
                                    readOnly={true}
                                    value={formData.createTime}
                                    labelEnable={false}
                                 />
                              </div>
                           </div>

                           <div className='col-span-12'>
                              <div className={comparisonRowClassName}>
                                 <div>
                                    <label className='block text-[#171717] mb-2'>
                                       {t('common.time-attendance-old')}
                                    </label>
                                    <CustomInput
                                       inputType='text'
                                       readOnly
                                       value={oldDateTimeText}
                                       labelEnable={false}
                                       className='w-full'
                                    />
                                 </div>
                                 {comparisonArrow}
                                 <div>
                                    <label className='block text-[#171717] mb-2'>
                                       {t('common.time-attendance-new')}
                                    </label>
                                    <CustomInput
                                       inputType='text'
                                       readOnly
                                       value={newDateTimeText}
                                       labelEnable={false}
                                       className='w-full'
                                    />
                                 </div>
                              </div>
                           </div>

                           <div className='col-span-12'>
                              <div className={comparisonRowClassName}>
                                 <div>
                                    <label className='block text-[#171717] mb-2'>
                                       {t('common.shift-old')}
                                    </label>
                                    <CustomInput
                                       inputType='text'
                                       readOnly={true}
                                       value={formData.shiftOld}
                                       labelEnable={false}
                                    />
                                 </div>
                                 {comparisonArrow}
                                 <div>
                                    <label className='block text-[#171717] mb-2'>
                                       {t('common.shift-new')}
                                    </label>
                                    <CustomInput
                                       inputType='text'
                                       readOnly={true}
                                       value={formData.shiftNew}
                                       labelEnable={false}
                                    />
                                 </div>
                              </div>
                           </div>

                           {/* <div className='col-span-12 flex flex-col gap-2'>
                              <label className='text-[#171717]'>{t('common.location')}</label>
                              <div className='w-full'>
                                 <CustomInput
                                    inputType='text'
                                    readOnly={true}
                                    value={formData.location}
                                    labelEnable={false}
                                 />
                              </div>
                           </div> */}
                        </>
                     ) : (
                        // Chỉ có newData (ADD_EVENT_LOG)
                        <>
                           <div className='col-span-12 md:col-span-6 flex flex-col gap-2'>
                              <label className='text-[#171717]'>{t('common.request-time')}</label>
                              <div className='w-full'>
                                 <CustomInput
                                    inputType='text'
                                    readOnly={true}
                                    value={formData.createTime}
                                    labelEnable={false}
                                 />
                              </div>
                           </div>

                           <div className='col-span-12 md:col-span-6 flex flex-col gap-2'>
                              <label className='text-[#171717]'>{t('common.time-attendance')}</label>
                              <div className='w-full'>
                                 <CustomInput
                                    inputType='text'
                                    readOnly
                                    value={newDateTimeText}
                                    labelEnable={false}
                                    className='w-full'
                                 />
                              </div>
                           </div>

                           <div className='col-span-12 md:col-span-12 flex flex-col gap-2'>
                              <label className='text-[#171717]'>{t('common.shift')}</label>
                              <div className='w-full'>
                                 <CustomInput
                                    inputType='text'
                                    readOnly={true}
                                    value={formData.shiftNew}
                                    labelEnable={false}
                                 />
                              </div>
                           </div>

                           {/* <div className='col-span-12 md:col-span-6 flex flex-col gap-2'>
                              <label className='shrink-0 text-[#171717]'>{t('common.location')}</label>
                              <div className='flex gap-4 w-full'>
                                 <div className='w-full'>
                                    <CustomInput
                                       inputType='text'
                                       readOnly={true}
                                       value={formData.location}
                                       labelEnable={false}
                                    />
                                 </div>
                              </div>
                           </div> */}
                        </>
                     )}

                     {/* Request reason */}
                     <div className='col-span-12 flex flex-col gap-2'>
                        <label className='text-[#171717]'>{t('common.request-reason')}</label>
                        <Tooltip
                           title={
                              <div>
                                 <div>{formData.descriptionOld}</div>
                              </div>
                           }
                           placement='top'
                           arrow
                        >
                           <div className='w-full'>
                              <CustomInput
                                 name='descriptionOld'
                                 inputType='text'
                                 value={formData.descriptionOld}
                                 labelEnable={false}
                                 readOnly={true}
                                 sx={{
                                    "& input": {
                                       whiteSpace: "nowrap",
                                       overflow: "hidden",
                                       textOverflow: "ellipsis",
                                    },
                                 }}
                              />
                           </div>
                        </Tooltip>
                     </div>

                     {/* Approve / reject reason */}
                     <div className='col-span-12 flex flex-col gap-2'>
                        <label className=''>
                           {t('common.approve-reject-reason')}
                           <span className='text-[#EF4444] ml-1'>*</span>
                        </label>
                        <div className='w-full'>
                           <CustomInput
                              name='description'
                              inputType='text'
                              value={formData.description}
                              handleInputChange={handleInputChange}
                              labelEnable={false}
                              placeholder={t('common.approve-reject-reason')}
                           />

                           {/* <div
                              className={`flex justify-end text-xs mt-1 ${formData.description?.length >= MAX_REASON_LENGTH
                                 ? "text-red-500"
                                 : "text-gray-500"
                                 }`}
                           >
                              {formData.description?.length || 0}/{MAX_REASON_LENGTH}
                           </div> */}
                        </div>
                     </div>

                     <div className="col-span-12 flex flex-col gap-2">
                        <div className="flex items-center justify-between mb-3 bg-[#F5F8FF] p-4 rounded-md border-l-[6px] border-l-[#0057FF]">
                           <p className="font-semibold">
                              {t("common.event-images")}
                           </p>

                           <button
                              type="button"
                              onClick={() => setIsImageSectionOpen(prev => !prev)}
                              className="text-sm font-medium hover:underline"
                           >
                              {isImageSectionOpen
                                 ? <ExpandMoreIcon sx={{ fontSize: 18 }} />
                                 : <ExpandLessIcon sx={{ fontSize: 18 }} />}
                           </button>
                        </div>

                        {isImageSectionOpen && (
                           <>
                              {dataIMG?.length === 0 ? (
                                 <div className="flex flex-col justify-center items-center h-[200px] gap-2 text-gray-400">
                                    <ImageIcon style={{ height: 45, width: 45 }} />
                                    {t("common.no-image")}
                                 </div>
                              ) : (
                                 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                                    {dataIMG.map((item) => {
                                       const isSelected = selectedImageId?.uniqueId === item.uniqueId;

                                       return (
                                          <div key={item.uniqueId}>
                                             <AvatarCard
                                                {...item}
                                                isSelected={isSelected}
                                                onSelect={() =>
                                                   setSelectedImageId(prev =>
                                                      prev?.uniqueId === item.uniqueId ? null : item
                                                   )
                                                }
                                             />
                                          </div>
                                       );
                                    })}
                                 </div>
                              )}

                              <div className="flex justify-center mt-2">
                                 <Pagination
                                    page={page + 1}
                                    count={Math.ceil(rowCount / RECORD_PER_PAGE)}
                                    onChange={(event, value) => {
                                       document.activeElement?.blur();
                                       setPage(value - 1);
                                    }}
                                    color="primary"
                                 />
                              </div>
                           </>
                        )}
                     </div>
                  </div>
               </CustomTabPanel>

               <CustomTabPanel value={tabValue} index={1}>
                  <div className="mt-4 relative">

                     {!parsedHistory || parsedHistory.length === 0 ? (
                        <div className="text-center text-gray-500 py-10">
                           {t("common.no-data")}
                        </div>
                     ) : (
                        parsedHistory.map((item, index) => {

                           const data = item.data || {};

                           return (
                              <div key={index} className="flex gap-4 relative pb-8">

                                 {index !== parsedHistory.length - 1 && (
                                    <div className="absolute left-[19px] top-10 w-[2px] h-full bg-gray-200" />
                                 )}

                                 {/* Timeline icon */}
                                 <div className="flex flex-col items-center z-10">
                                    <div
                                       className={`w-10 h-10 rounded-full flex items-center justify-center border
                              ${item.requestStatusName === "APPROVED"
                                             ? "bg-[#F0FDF4] border-[#E5E7EB]"
                                             : item.requestStatusName === "REJECTED"
                                                ? "bg-[#FEF2F2] border-[#E5E7EB]"
                                                : "bg-[#FEF2E6] border-[#E5E7EB]"
                                          }`}
                                    >
                                       <div
                                          className={`w-6 h-6 rounded-full flex items-center justify-center
                                ${item.requestStatusName === "APPROVED"
                                                ? "bg-green-500"
                                                : item.requestStatusName === "REJECTED"
                                                   ? "bg-red-500"
                                                   : "bg-orange-400"
                                             }`}
                                       >
                                          {item.requestStatusName === "APPROVED" && (
                                             <DoneIcon sx={{ color: "#fff", fontSize: 16 }} />
                                          )}

                                          {item.requestStatusName === "REJECTED" && (
                                             <ClearIcon sx={{ color: "#fff", fontSize: 16 }} />
                                          )}

                                          {(item.requestStatusName === "PARTNER_PENDING" ||
                                             item.requestStatusName === "PENDING" ||
                                             !["APPROVED", "REJECTED", "PARTNER_REJECTED"].includes(
                                                item.requestStatusName
                                             )) && (
                                                <AccessTimeIcon sx={{ color: "#fff", fontSize: 16 }} />
                                             )}
                                       </div>
                                    </div>
                                 </div>

                                 {/* Content */}
                                 <div className="flex-1 bg-white">

                                    {/* Title + createdTime (giống LeaveAndHoliday) */}
                                    <div className="flex items-start justify-between gap-3">
                                       <p className="font-semibold text-gray-900">
                                          {getFullNameByEmail(data.createdBy)}{" "}

                                          <span className="font-normal text-[#1F2937]">
                                             {item.requestStatusName === "APPROVED"
                                                ? t("common.approvedRequestEventLog")
                                                : item.requestStatusName === "REJECTED" || item.requestStatusName === "PARTNER_REJECTED"
                                                   ? t("common.rejectedRequestEventLog")
                                                   : item.requestStatusName === "PENDING"
                                                      ? t("common.pendingRequest")
                                                      : t("common.sentRequest")}
                                          </span>
                                       </p>
                                       <p className="text-sm text-[#6B7280] whitespace-nowrap">
                                          {item.createdAtLabel || "-"}
                                       </p>
                                    </div>

                                    <p className="text-sm text-gray-600 mt-1">
                                       <span className="text-gray-500">
                                          {t("common.shift")}:
                                       </span>{" "}
                                       <span className="text-[#1F2937]">
                                          {data.shiftName === "DEFAULT"
                                             ? t("common.default")
                                             : data.shiftName || "-"}
                                       </span>{" "}
                                    </p>

                                    <p className="text-sm text-gray-600">
                                       <span
                                          style={{ color: data.eventTypeColor }}
                                          className="font-medium"
                                       >
                                          {data.eventTypeName || "-"}
                                       </span>{" "}
                                       <span className="text-[#1F2937]">
                                          {data.attendanceDate || "-"}
                                       </span>
                                    </p>

                                    {/* Reason */}
                                    <p className="text-sm text-gray-600">
                                       <span className="text-gray-500">
                                          {t("common.reason")}:
                                       </span>{" "}
                                       {item.requestStatusName === "PARTNER_PENDING"
                                          ? item.requestReason
                                          : item.requestStatusName === "APPROVED"
                                             ? (item.responseReason || item.requestReason)
                                             : (item.responseReason || item.requestReason)}
                                    </p>

                                 </div>
                              </div>
                           );
                        })
                     )}


                  </div>
               </CustomTabPanel>

            </DialogContent>

            <DialogActions>
               {tabValue === 0 && safeInfo?.requestStatusName === "PENDING" && (
                  <div className='flex gap-2 px-4 py-4'>
                     <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isLoading}>
                        <p>{t('button.close')}</p>
                     </ControlpanelSecondaryButton>
                     <ControlpanelErrorButton
                        onClick={handleOpenRejectRequest}
                        disabled={isLoading || !formData.description}
                     >
                        <p>{t('button.reject')}</p>
                     </ControlpanelErrorButton>
                     <ControlpanelSuccessButton
                        onClick={handleOpenApproveRequest}
                        disabled={isLoading || !formData.description}
                        style={{
                           color: '#fff',
                           opacity: !formData.description ? '0.5' : '1',
                        }}
                     >
                        <p>{t('button.approve')}</p>
                     </ControlpanelSuccessButton>
                  </div>
               )}

               {(tabValue === 1 || safeInfo?.requestStatusName !== "PENDING") && (
                  <div className='flex gap-2 px-4 py-4'>
                     <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isLoading}>
                        <p>{t('button.close')}</p>
                     </ControlpanelSecondaryButton>
                  </div>
               )}

            </DialogActions>
         </CustomDialog>

         {openCorfirmApproveRequest && (
            <CorfirmApproveRequestDialog
               onClose={handleCloseApproveRequest}
               formData={formData}
               onRefresh={onRefresh}
               onCloseDetail={handleCloseDialog}
               selectedImageId={selectedImageId}
               onLoadingChange={(loading) => setLoadingCount((prev) => Math.max(prev + (loading ? 1 : -1), 0))}
            />
         )}

         {openCorfirmRejectRequest && (
            <CorfirmRejectRequestDialog
               onClose={handleCloseRejectRequest}
               formData={formData}
               onRefresh={onRefresh}
               onCloseDetail={handleCloseDialog}
               onLoadingChange={(loading) => setLoadingCount((prev) => Math.max(prev + (loading ? 1 : -1), 0))}
            />
         )}
      </>
   );
};

export default DetailRequestDialog;
